 import { useState } from "react";
import {
  Avatar,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation, useNavigate } from "react-router-dom";

import assets from "../../assets";
import sizeConfigs from "../../configs/sizeConfigs";
import appRoutes from "../../routes/appRoutes";

type SidebarPropsType = {
  displayText: string;
  icon?: React.ReactNode;
};

type AppRouteType = {
  path?: string;
  child?: AppRouteType[];
  sidebarProps?: SidebarPropsType;
};

type SidebarItemComponentProps = {
  item: AppRouteType;
};

const SidebarItem = ({ item }: SidebarItemComponentProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const active = item.path ? location.pathname === item.path : false;

  return (
    <ListItemButton
      onClick={() => item.path && navigate(item.path)}
      sx={{
        borderRadius: "30px",
        py: 1.2,
        px: 2,
        color: active ? "#1976d2" : "#ffffff",
        backgroundColor: active ? "#ffffff" : "transparent",
        transition: "all 0.3s ease",
        "&:hover": {
          backgroundColor: active ? "#ffffff" : "rgba(255,255,255,0.16)"
        }
      }}
    >
      {item.sidebarProps?.icon && (
        <ListItemIcon
          sx={{
            color: active ? "#1976d2" : "#ffffff",
            minWidth: "38px"
          }}
        >
          {item.sidebarProps.icon}
        </ListItemIcon>
      )}

      <ListItemText
        primary={item.sidebarProps?.displayText}
        primaryTypographyProps={{
          fontSize: 13,
          fontWeight: active ? 700 : 500
        }}
      />
    </ListItemButton>
  );
};

const SidebarItemCollapse = ({ item }: SidebarItemComponentProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeChild = item.child?.some((child) => child.path === location.pathname) || false;

  const [open, setOpen] = useState(activeChild);

  return (
    <>
      <ListItemButton
        onClick={() => setOpen(!open)}
        sx={{
          borderRadius: "30px",
          py: 1.2,
          px: 2,
          color: activeChild || open ? "#1976d2" : "#ffffff",
          backgroundColor: activeChild || open ? "#ffffff" : "transparent",
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor:
              activeChild || open ? "#ffffff" : "rgba(255,255,255,0.16)"
          }
        }}
      >
        {item.sidebarProps?.icon && (
          <ListItemIcon
            sx={{
              color: activeChild || open ? "#1976d2" : "#ffffff",
              minWidth: "38px"
            }}
          >
            {item.sidebarProps.icon}
          </ListItemIcon>
        )}

        <ListItemText
          primary={item.sidebarProps?.displayText}
          primaryTypographyProps={{
            fontSize: 13,
            fontWeight: 700
          }}
        />

        <KeyboardArrowDownIcon
          sx={{
            color: activeChild || open ? "#1976d2" : "#ffffff",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease"
          }}
        />
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          disablePadding
          sx={{
            mt: 0.5,
            display: "flex",
            flexDirection: "column",
            gap: 0.5
          }}
        >
          {item.child?.map((child, index) => {
            const childActive = child.path === location.pathname;

            return (
              <ListItemButton
                key={index}
                onClick={() => child.path && navigate(child.path)}
                sx={{
                  ml: 2,
                  mr: 0.5,
                  borderRadius: "22px",
                  py: 1,
                  px: 2,
                  color: childActive ? "#1976d2" : "#ffffff",
                  backgroundColor: childActive ? "#ffffff" : "rgba(255,255,255,0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: childActive
                      ? "#ffffff"
                      : "rgba(255,255,255,0.16)"
                  }
                }}
              >
                <ListItemText
                  primary={child.sidebarProps?.displayText}
                  primaryTypographyProps={{
                    fontSize: 12.5,
                    fontWeight: childActive ? 700 : 500
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </>
  );
};

const Sidebar = () => {
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: sizeConfigs.sidebar.width,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: sizeConfigs.sidebar.width,
          boxSizing: "border-box",
          border: "none",
          borderTopRightRadius: "30px",
          borderBottomRightRadius: "30px",
          background: "linear-gradient(180deg, #39A7F7 0%, #2196F3 100%)",
          color: "#ffffff",
          padding: "10px 12px",
          overflowY: "auto",
          overflowX: "hidden"
        }
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: "64px",
          px: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Avatar
            src={assets.images.logo}
            alt="Logo"
            sx={{
              width: 34,
              height: 34,
              bgcolor: "transparent"
            }}
          />
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: "#ffffff"
            }}
          >
            Eldorado Brasil
          </Typography>
        </Stack>

        <IconButton
          sx={{
            color: "#ffffff",
            border: "1px solid rgba(255,255,255,0.7)",
            width: 38,
            height: 38
          }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Toolbar>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.25)", mb: 1.5 }} />

      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: 1,
          py: 1.5
        }}
      >
        <Avatar
         // src={assets.images.avatar}
          alt="Usuário"
          sx={{ width: 38, height: 38 }}
        />
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: 500,
            color: "#ffffff"
          }}
        >
          Márcio Oliveira
        </Typography>
      </Stack>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.25)", mb: 2 }} />

      <List
        disablePadding
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          px: 0.5
        }}
      >
        {appRoutes.map((route: AppRouteType, index: number) =>
          route.sidebarProps ? (
            route.child && route.child.length > 0 ? (
              <SidebarItemCollapse item={route} key={index} />
            ) : (
              <SidebarItem item={route} key={index} />
            )
          ) : null
        )}
      </List>
    </Drawer>
  );
};

export default Sidebar;